/** Countdown urgency tiers for Mock Exam (hard gate). */
export type CountdownUrgency = 'normal' | 'notice' | 'urgent' | 'critical';

/** Soft writing progress vs suggested minutes (never a hard gate). */
export type SoftTargetPhase = 'under' | 'near' | 'over';

const MINUTE_MS = 60_000;

export const COUNTDOWN_NOTICE_MS = 15 * MINUTE_MS;
export const COUNTDOWN_URGENT_MS = 5 * MINUTE_MS;
export const COUNTDOWN_CRITICAL_MS = 1 * MINUTE_MS;

/** Near suggested time when elapsed ≥ 85% of target. */
export const SOFT_TARGET_NEAR_RATIO = 0.85;

export function resolveCountdownUrgency(remainingMs: number): CountdownUrgency {
  const remaining = Math.max(0, remainingMs);
  if (remaining <= COUNTDOWN_CRITICAL_MS) return 'critical';
  if (remaining <= COUNTDOWN_URGENT_MS) return 'urgent';
  if (remaining <= COUNTDOWN_NOTICE_MS) return 'notice';
  return 'normal';
}

/**
 * Remaining fraction of a fixed budget (1 = full time left, 0 = expired).
 * When totalMs is missing/invalid, returns null (no ring).
 */
export function resolveCountdownProgress(
  remainingMs: number,
  totalMs: number | null | undefined
): number | null {
  if (totalMs == null || totalMs <= 0) return null;
  return Math.min(1, Math.max(0, remainingMs / totalMs));
}

export function resolveSoftTargetPhase(
  elapsedMs: number,
  targetMs: number | null | undefined
): SoftTargetPhase | null {
  if (targetMs == null || targetMs <= 0) return null;
  const elapsed = Math.max(0, elapsedMs);
  if (elapsed >= targetMs) return 'over';
  if (elapsed >= targetMs * SOFT_TARGET_NEAR_RATIO) return 'near';
  return 'under';
}

/**
 * Elapsed / suggested (capped at 1 for the ring; over-target still returns 1).
 * Null when there is no suggested target.
 */
export function resolveSoftTargetProgress(
  elapsedMs: number,
  targetMs: number | null | undefined
): number | null {
  if (targetMs == null || targetMs <= 0) return null;
  return Math.min(1, Math.max(0, elapsedMs / targetMs));
}

export function softTargetStatusLabel(phase: SoftTargetPhase | null): string | null {
  if (phase === 'over') return '已超过建议用时';
  if (phase === 'near') return '接近建议用时';
  return null;
}
